<script>
import Layout from '@/Shared/Layout'
export default { layout: Layout }
</script>

<script setup>
import { onBeforeUnmount, onMounted, provide, ref } from 'vue'
import { Head } from '@inertiajs/inertia-vue3'
import HomeSlide from '@/Pages/Home/HomeSlide'
import { useStore } from '@/store/bucket'
import useIntersectionObserver from '@/Shared/useIntersectionObserver.js'
import usePref from '@/Shared/usePref.js'

const props = defineProps({
  title: String,
  latestNews: Array,
  brandList: Array,
  shopList: Array,
  storyList: Object,
  pref: Object,
})

provide('brand-list', props.brandList)
provide('shop-list', props.shopList)

const htmlRef = ref()
const sectionRefs = ref([])
const topRef = ref()
const messageRef = ref([])
const newsRef = ref()
const brandsRef = ref()
const shopsRef = ref()

const addHomeClass = () => {
  htmlRef.value = document.getElementsByTagName('html')[0]
  htmlRef.value.classList.add('home')
}

const observeSection = () => {
  sectionRefs.value = [topRef.value, messageRef.value, newsRef.value, brandsRef.value, shopsRef.value]
  useIntersectionObserver({
    target: sectionRefs.value,
    root: document.querySelector('.main-body'),
    threshold: 0.5,
    onEnter: (entry) => {
      const hash = entry.target.dataset.id === 'top' ? '#' : `#${entry.target.dataset.id}`
      window.location = hash
    },
  })
}

const observeText = () => {
  useIntersectionObserver({
    target: messageRef.value,
    threshold: [0.1, 0.25, 0.5, 0.75, 1],
    onEnter: (entry) => {
      const pList = entry.target.querySelectorAll('p')
      pList.forEach((p) => {
        p.classList.remove('opacity-0', 'translate-y-5')
      })
    },
  })
}

const scrollToTarget = (hash) => {
  if (!location.hash) return
  const dataID = hash.replace('#', '')
  const target = document.querySelector('[data-id="' + dataID + '"]')
  target.scrollIntoView({ block: 'center' })
}

onMounted(() => {
  usePref(props.pref)
  useStore().updateNewsList(props.latestNews)
  addHomeClass()
  observeSection()
  observeText()
  scrollToTarget(location.hash)
})

onBeforeUnmount(() => {
  htmlRef.value.classList.remove('home')
})

const style = 'relative z-10 min-h-[calc(100dvh_-_55px)] md:min-h-[calc(100dvh_-_75px)] xl:min-h-screen snap-start snap-always grid place-content-center overflow-hidden'

const texts = {
  1: '資源には限りがあり、<br />自然は常に破壊され続けている――。',
  2: 'かけがえのない存在だと気づいた時から、<br />バックパッキングの旅はスタートします。',
  3: '大自然の営みのなか、<br />自らが持つ道具と知識を頼りに<br />サバイバルする、ローインパクトの旅。',
  4: '地球を愛し、未知の体験、<br />新しい発見を求めて原野に分け入る冒険者を、<br />A&amp;Fはサポートいたします。',
}
</script>

<template>
  <Head :title="props.title" />
  <section ref="topRef" data-id="top" :class="style">
    <img class="animate-fade-in block m-auto mt-[-30px] xl:mt-0 max-w-[60vw]" src="/img/home/text_welcome.png" alt="Welcome to A&amp;F Contory" />
  </section>
  <section ref="messageRef" data-id="message" :class="style">
    <p v-for="(text, index) in texts" :key="text" :style="'transition-delay:' + (index - 1) * 0.3 + 's;'" class="font-jpsans text-center text-white font-medium text-[3.8vw] xs:text-[16px] sm:text-[17px] md:text-[18px] lg:text-[19px] xl:text-[20px] tracking-[0.01em] leading-[2.4] mb-[2em] opacity-0 translate-y-5 transition-all [transition-duration:2s] linear" v-html="text" />
  </section>
  <section ref="newsRef" data-id="news" :class="style" class="bg-white">
    <HomeSlide :data="props.latestNews" category="news" title-en="NEWS" title-ja="新着ニュース" />
  </section>
  <section ref="brandsRef" data-id="brands" :class="style" class="bg-afgreen [&_h2]:text-white">
    <HomeSlide :data="props.brandList" category="brand" title-en="BRANDS" title-ja="ブランド" />
  </section>
  <section ref="shopsRef" data-id="shops" :class="style" class="bg-white">
    <HomeSlide :data="props.shopList" category="shop" title-en="STORES" title-ja="店舗情報" />
  </section>
</template>
